import { PackageType } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';

/**
 * Front-end mirror of the server-side (Smithy/Coral) constraints on a
 * PackageBuildInfo, so the UI can catch input mistakes BEFORE submitting and
 * show a friendly, actionable message instead of the raw Coral validation
 * error (e.g. "1 validation error detected: Value 'f418...' at
 * 'packageBuildInfoList.1.member.commitId' failed to satisfy constraint:
 * Member must satisfy regular expression pattern: [0-9a-f]{40}").
 *
 * Keep these in sync with the model definitions in
 * AWSDevDocsVirtualSmileyConferenceToolModel/model/type.smithy:
 *   PackageName  @pattern("^[A-Za-z][A-Za-z0-9-_]{2,79}$")
 *   BranchName   @length(min: 1, max: 256) @pattern("^[A-Za-z0-9._/\\-]+$")
 *   CommitId     @pattern("^[0-9a-f]{40}$")
 * On PackageBuildInfo, packageName / commitId / branch / packageType are
 * @required; publishingBranch is optional but must be a valid BranchName when
 * provided.
 *
 * These patterns are the single source of truth for branch/commit/package-name
 * validation in the UI: `validatePackages` uses them for the authoritative
 * pre-submit check (with friendly errors), and PackageForm / PackageSuggester
 * import them directly for inline field highlighting as the user types. (They
 * replaced the old, looser `src/patterns.ts` regexes, which were removed.)
 */
export const PACKAGE_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{2,79}$/;
export const BRANCH_NAME_PATTERN = /^[A-Za-z0-9._/-]+$/;
export const BRANCH_NAME_MAX_LENGTH = 256;
export const COMMIT_ID_PATTERN = /^[0-9a-f]{40}$/;

// Minimal shape of a package row this module validates. Compatible with ProtoPBI
// (whose packageType may be the empty string before a type is selected).
export interface ValidatablePackage {
  packageName?: string;
  packageType?: PackageType | '';
  branch?: string;
  commitId?: string;
  publishingBranch?: string;
}

const BRANCH_CHARSET_HELP =
  'Branch names must be 1–256 characters and may contain only letters, numbers, dots, hyphens, underscores, and slashes.';

// Human-friendly label for a package row: its name if it has one, otherwise its
// 1-based position in the list so unnamed rows are still identifiable.
const describePackage = (pkg: ValidatablePackage, index: number): string =>
  pkg.packageName?.trim() ? `"${pkg.packageName.trim()}"` : `#${index + 1}`;

/**
 * Validate a package list against the server's PackageBuildInfo constraints.
 * Returns a list of friendly, actionable error messages (one per problem);
 * an empty array means the list is valid and safe to submit.
 *
 * An empty package list is intentionally allowed: submitting zero packages is a
 * valid API operation that clears the entity's build info (mirrors what the
 * service accepts). Only a *populated* row with a missing/invalid required field
 * is a validation failure, matching the Model's @required + @pattern rejections.
 */
export const validatePackages = (items: ValidatablePackage[]): string[] => {
  const errors: string[] = [];

  items.forEach((pkg, index) => {
    const who = describePackage(pkg, index);
    const packageName = pkg.packageName?.trim() ?? '';
    const branch = pkg.branch?.trim() ?? '';
    const commitId = pkg.commitId?.trim() ?? '';
    const publishingBranch = pkg.publishingBranch?.trim() ?? '';

    // packageName — required; ^[A-Za-z][A-Za-z0-9-_]{2,79}$ (3–80 chars, letter-led).
    if (!packageName) {
      errors.push(`Package ${who}: enter a package name.`);
    } else if (!PACKAGE_NAME_PATTERN.test(packageName)) {
      errors.push(
        `Package ${who}: package name "${packageName}" is invalid. It must be 3–80 characters, ` +
          `start with a letter, and contain only letters, numbers, hyphens, and underscores.`
      );
    }

    // packageType — required.
    if (!pkg.packageType) {
      errors.push(`Package ${who}: select a package type.`);
    }

    // branch — required; BranchName charset + max length.
    if (!branch) {
      errors.push(`Package ${who}: enter a branch.`);
    } else if (
      branch.length > BRANCH_NAME_MAX_LENGTH ||
      !BRANCH_NAME_PATTERN.test(branch)
    ) {
      errors.push(
        `Package ${who}: branch "${branch}" is invalid. ${BRANCH_CHARSET_HELP}`
      );
    }

    // commitId — required; exactly 40 lowercase-hex characters.
    if (!commitId) {
      errors.push(`Package ${who}: enter a commit ID.`);
    } else if (!COMMIT_ID_PATTERN.test(commitId)) {
      errors.push(
        `Package ${who}: commit ID "${commitId}" is invalid. A commit ID must be exactly ` +
          `40 characters long and use only the digits 0–9 and lowercase letters a–f.`
      );
    }

    // publishingBranch (destination branch) — optional, but a valid BranchName when set.
    if (
      publishingBranch &&
      (publishingBranch.length > BRANCH_NAME_MAX_LENGTH ||
        !BRANCH_NAME_PATTERN.test(publishingBranch))
    ) {
      errors.push(
        `Package ${who}: destination branch "${publishingBranch}" is invalid. ${BRANCH_CHARSET_HELP}`
      );
    }
  });

  return errors;
};
